// 대화목록 필터/검색/제목편집 + 대화 열기·새 대화·삭제·즐겨찾기·보관·태그 등 목록 액션.
// 핵심 상태(conversations/currentConvId/messages)는 core, 스트림 정리는 useChatMessage.detachStream를 주입받는다.
import { useRef, useState } from 'react';
import { historyAPI } from '../../../api/client';
import { alertDialog, confirmDialog, promptDialog } from '../../../components/AppDialog';

const normalizeTurnSnapshot = (message) => {
  const raw = message?.turn_snapshot || message?.execution_snapshot;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return {
      version: raw.version ?? 1,
      status: raw.status || message?.turn_status || 'legacy',
      attachments: Array.isArray(raw.attachments) ? raw.attachments : [],
      ...raw,
    };
  }
  return {
    version: 1,
    status: message?.turn_status || 'legacy',
    attachments: [],
  };
};

const normalizeMessages = (messages) => (
  Array.isArray(messages)
    ? messages.map((message) => {
        const row = message || {};
        const citations = (
          Object.prototype.hasOwnProperty.call(row, 'citations')
          || Object.prototype.hasOwnProperty.call(row, 'citations_partial')
        ) ? {
          citations: Array.isArray(row.citations) ? row.citations : [],
          citations_partial: !!row.citations_partial,
        } : {};
        return {
          ...row,
          ...citations,
          files: Array.isArray(row.files) ? row.files : [],
          turn_snapshot: normalizeTurnSnapshot(row),
        };
      })
    : []
);

export const normalizeConversationLoad = (data, fallbackConvId = null) => {
  if (Array.isArray(data)) {
    return { conversation: null, messages: normalizeMessages(data) };
  }
  const conversation = data?.conversation || null;
  return {
    conversation: conversation
      ? {
          ...conversation,
          id: conversation.id ?? fallbackConvId,
          project_id: conversation.project_id ?? null,
          access_role: conversation.access_role ?? null,
        }
      : null,
    messages: normalizeMessages(data?.messages),
  };
};

const syncConversationUrl = (convId, { replace = false } = {}) => {
  if (typeof window === 'undefined' || !convId) return;
  const url = new URL(window.location.href);
  url.searchParams.set('conv', String(convId));
  url.searchParams.delete('project');
  window.history[replace ? 'replaceState' : 'pushState'](
    { chatConvId: convId },
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
};

const clearConversationUrl = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('conv')) return;
  url.searchParams.delete('conv');
  window.history.pushState(
    { chatConvId: null },
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
};

export default function useConversation({
  setConversations, currentConvId, setCurrentConvId, setMessages,
  detachStream, setPendingFiles, setRagSources, atBottomRef, setShowScrollDown, setConvDrawerOpen,
  setSelectedProjectId = () => {}, setCurrentProjectAccessRole = () => {}, resetWorkspaceForProject = () => {},
}) {
  const [favOnly, setFavOnly]             = useState(false);       // 즐겨찾기만 보기
  const [archiveView, setArchiveViewState] = useState(false);      // false/true/'trash'
  const [tagFilter, setTagFilter]         = useState(null);        // 선택된 태그 필터
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = 검색 안 함
  const [editingTitleId,  setEditingTitleId]  = useState(null);
  const [editingTitleVal, setEditingTitleVal] = useState('');
  const loadGenerationRef = useRef(0);
  const listGenerationRef = useRef(0);

  const reloadConversations = async () => {
    const generation = ++listGenerationRef.current;
    const requestView = archiveView;
    try {
      const r = requestView === 'trash'
        ? await historyAPI.listTrash()
        : await historyAPI.listConversations();
      if (generation === listGenerationRef.current) setConversations(r.data);
    } catch {}
  };

  const setArchiveView = async (next) => {
    const value = typeof next === 'function' ? next(archiveView) : next;
    const generation = ++listGenerationRef.current;
    if (value !== 'trash' && archiveView !== 'trash') {
      setArchiveViewState(value);
      setSearchQuery('');
      setSearchResults(null);
      return;
    }
    try {
      const r = value === 'trash'
        ? await historyAPI.listTrash()
        : await historyAPI.listConversations();
      if (generation !== listGenerationRef.current) return;
      setConversations(r.data);
      setArchiveViewState(value);
      setSearchQuery('');
      setSearchResults(null);
    } catch {
      if (generation !== listGenerationRef.current) return;
      await alertDialog(value === 'trash' ? '휴지통을 불러오지 못했습니다.' : '대화 목록을 불러오지 못했습니다.');
    }
  };

  const loadConversation = async (convId, options = {}) => {
    const generation = ++loadGenerationRef.current;
    detachStream();
    setConvDrawerOpen(false);   // 모바일: 대화 선택 시 드로어 닫기
    const r = await historyAPI.getMessages(convId);
    if (generation !== loadGenerationRef.current) return;
    const { conversation, messages } = normalizeConversationLoad(r.data, convId);
    const loadedConvId = conversation?.id ?? convId;
    const loadedProjectId = conversation?.project_id ?? null;
    const loadedAccessRole = conversation?.access_role ?? null;
    atBottomRef.current = true; setShowScrollDown(false);   // 대화 열면 맨 아래에서 시작
    setCurrentConvId(loadedConvId);
    setSelectedProjectId(loadedProjectId);
    setCurrentProjectAccessRole(loadedAccessRole);
    resetWorkspaceForProject(loadedProjectId);
    setMessages(messages);
    setPendingFiles([]);
    setRagSources(null);   // 참고문서 카드는 현재 스트림 한정(히스토리엔 미저장)
    if (options.syncUrl !== false) syncConversationUrl(loadedConvId, { replace: !!options.replaceUrl });
  };

  const saveTitle = async (convId) => {
    const val = editingTitleVal.trim();
    if (!val) { setEditingTitleId(null); return; }
    await historyAPI.updateConversation(convId, { title: val }).catch(() => {});
    setConversations((prev) => prev.map((c) => c.id === convId ? { ...c, title: val } : c));
    setSearchResults((prev) => prev ? prev.map((c) => c.id === convId ? { ...c, title: val } : c) : null);
    setEditingTitleId(null);
  };

  const startNew = (options = {}) => {
    loadGenerationRef.current++;
    detachStream();
    setConvDrawerOpen(false);   // 모바일: 새 대화 시작 시 드로어 닫기
    setCurrentConvId(null);
    setMessages([]);
    setPendingFiles([]);
    setRagSources(null);
    setSearchQuery('');
    setSearchResults(null);
    if (options.syncUrl !== false) clearConversationUrl();
  };

  const handleDeleteConv = async (convId, e, action = 'trash') => {
    e.stopPropagation();
    const restore = action === 'restore';
    const permanent = action === 'permanent';
    if (!restore) {
      const prompt = permanent
        ? '이 대화를 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.'
        : '이 대화를 휴지통으로 이동하시겠습니까? 30일 동안 복원할 수 있습니다.';
      if (!(await confirmDialog(prompt, { danger: true }))) return;
    }
    try {
      if (restore) await historyAPI.restoreConversation(convId);
      else if (permanent) await historyAPI.permanentlyDeleteConversation(convId);
      else await historyAPI.deleteConversation(convId);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (!permanent && currentConvId === convId) startNew();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message;
      await alertDialog(message || (restore
        ? '대화를 복원하지 못했습니다.'
        : permanent ? '대화를 영구 삭제하지 못했습니다.' : '대화를 삭제하지 못했습니다.'));
    }
  };

  const toggleFavorite = async (c, e) => {
    e.stopPropagation();
    const next = !c.is_favorite;
    setConversations((prev) => prev.map((x) => x.id === c.id ? { ...x, is_favorite: next } : x));
    try { await historyAPI.updateConversation(c.id, { is_favorite: next }); }
    catch { setConversations((prev) => prev.map((x) => x.id === c.id ? { ...x, is_favorite: !next } : x)); }
  };

  const toggleArchive = async (c, e) => {
    e.stopPropagation();
    const next = !c.is_archived;
    setConversations((prev) => prev.map((x) => x.id === c.id ? { ...x, is_archived: next } : x));
    try { await historyAPI.updateConversation(c.id, { is_archived: next }); }
    catch { setConversations((prev) => prev.map((x) => x.id === c.id ? { ...x, is_archived: !next } : x)); }
  };

  const editTags = async (c, e) => {
    e.stopPropagation();
    const input = await promptDialog('태그를 콤마(,)로 구분해 입력하세요. (예: 중요, 물류, CS)', c.tags || '');
    if (input === null) return;
    try {
      await historyAPI.updateConversation(c.id, { tags: input });
      const r = await historyAPI.listConversations();
      setConversations(r.data);
    } catch { alertDialog('태그 저장에 실패했습니다.'); }
  };

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults(null); return; }
    historyAPI.search(q.trim()).then((r) => setSearchResults(r.data)).catch(() => {});
  };

  return {
    favOnly, setFavOnly, archiveView, setArchiveView, tagFilter, setTagFilter,
    searchQuery, searchResults, editingTitleId, setEditingTitleId, editingTitleVal, setEditingTitleVal,
    reloadConversations, loadConversation, saveTitle, startNew,
    handleDeleteConv, toggleFavorite, toggleArchive, editTags, handleSearch,
  };
}
